import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { UserId } from "@visioni/a3s-users";
import { revokeUserAccessMutation } from "../mutations";
import { accessManagementQuery } from "../queries";

export interface RevokeUserAccessInput {
  user: string;
}

/**
 * Hook to revoke all of a user's access in the current tenant — every role
 * assignment and every direct camera permission — in a single server call.
 * Automatically invalidates the access management query on success so the
 * caller can navigate away immediately while the list refetches in the
 * background.
 *
 * Accepts plain string `user` IDs — parsing to `UserId` is handled internally.
 * Server-side the endpoint rejects self-revoke with a 403.
 */
export function useRevokeUserAccess() {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationApiConfig = {
    tenantId: apiConfig.tenant?.tenantId,
    apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
    authenticatedFetch: apiConfig.authenticatedFetch,
  };

  const base = revokeUserAccessMutation(mutationApiConfig, {});

  return useMutation({
    ...base,
    mutationFn: (input: RevokeUserAccessInput) => base.mutationFn({ user: UserId.fromString(input.user) }),
    onSuccess: () => {
      const queryKey = accessManagementQuery(apiConfig, {}).queryKey;
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
